import {
  TicketCategoriesModel,
  TicketCategoryModel,
  TicketEmergencyRule,
  TicketOwnerStepModel,
  TicketPriority
} from '../../models/ticket.model';

/**
 * Ticket rules the screens need before they ask the server: what a form may
 * offer. The server (server.integrator.helpdesk, ticket.rules.ts) still enforces
 * each one. These only keep the screens from offering a choice it will refuse.
 */

/** Lowest to highest. P1 is the most urgent. */
export const PRIORITY_ORDER: TicketPriority[] = ['P4', 'P3', 'P2', 'P1'];

const rank = (priority: TicketPriority): number => PRIORITY_ORDER.indexOf(priority);

/**
 * The priorities a ticket may be created at: the category default or higher.
 * Lowering a category's priority is refused, because the default is what CX
 * agreed that kind of problem is worth.
 */
export function creatablePriorities(categoryDefault: TicketPriority): TicketPriority[] {
  return PRIORITY_ORDER.filter((p) => rank(p) >= rank(categoryDefault));
}

/** The priorities an open ticket may be raised to: strictly higher than now. */
export function raisablePriorities(current: TicketPriority): TicketPriority[] {
  return PRIORITY_ORDER.filter((p) => rank(p) > rank(current));
}

/**
 * The emergency checkbox: fixed for "never" and "always", the agent's call for
 * "optional". `value` is what is sent when the box is not the agent's to tick.
 */
export function emergencyChoice(rule: TicketEmergencyRule): { editable: boolean; value: boolean } {
  if (rule === 'always') return { editable: false, value: true };
  if (rule === 'never') return { editable: false, value: false };
  return { editable: true, value: false };
}

/**
 * Who a handoff can go to. Several teams (the sheet's "Finance / Insurance")
 * means the agent picks one, and the server refuses a handoff without the pick.
 */
export function handoffChoices(next?: TicketOwnerStepModel | null): {
  available: boolean;
  teams: string[];
  mustChoose: boolean;
  optional: boolean;
} {
  const teams = next?.teams ?? [];
  return {
    available: teams.length > 0,
    teams,
    mustChoose: teams.length > 1,
    optional: Boolean(next?.when)
  };
}

export interface CategoryOption {
  value: string;
  label: string;
  item: TicketCategoryModel;
}

export interface CategoryGroup {
  label: string;
  options: CategoryOption[];
}

/**
 * The category picker's groups: "Guest · Request", "Landlord / Owner · Inquiry", and so
 * on, in taxonomy order. `onlySegments` narrows it where the page already says who the
 * ticket is about. A reservation is a guest's, for example.
 */
export function categoryGroups(
  doc: TicketCategoriesModel,
  onlySegments?: string[]
): CategoryGroup[] {
  const groups: CategoryGroup[] = [];
  doc.items
    .filter((item) => !onlySegments || onlySegments.includes(item.segment))
    .forEach((item) => {
      const label = `${doc.segments[item.segment] ?? item.segment} · ${
        doc.kinds[item.kind] ?? item.kind
      }`;
      let group = groups.find((g) => g.label === label);
      if (!group) {
        group = { label, options: [] };
        groups.push(group);
      }
      group.options.push({
        value: item.code,
        label: `${item.code} · ${item.category} — ${item.subcategory}`,
        item
      });
    });
  return groups;
}

/** Mirrors the integrator's AttachmentDto limits: 5 files, 7 MB decoded in total. */
export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 7 * 1024 * 1024;

/** Why these files cannot be sent, or undefined when they can. */
export function attachmentProblem(files: { name: string; size: number }[]): string | undefined {
  if (files.length > MAX_ATTACHMENTS) return `At most ${MAX_ATTACHMENTS} files per note`;
  const total = files.reduce((sum, f) => sum + f.size, 0);
  if (total > MAX_ATTACHMENT_BYTES) {
    return `Files add up to ${formatBytes(total)}; the limit is ${formatBytes(
      MAX_ATTACHMENT_BYTES
    )}`;
  }
  const empty = files.find((f) => f.size === 0);
  if (empty) return `${empty.name} is empty`;
  return undefined;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
